import React, { useEffect, useState } from 'react';
import classNames from 'classnames/bind';
import dayjs from 'dayjs';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
} from 'antd';

import styles from './valves.module.scss';
import API from 'helpers/API';
import { valveManufacturerOptions, valveVersionOptions } from './formOptions';

const cx = classNames.bind(styles);

const toDayjs = (value) => (value ? dayjs(value, 'YYYYMMDD') : null);

const ValveHistoryModal = ({ rid, tank, open, onClose, onChanged }) => {
  const [valves, setValves] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingValve, setEditingValve] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    serialNo: '',
    manufacturer: '',
    version: '',
    manufacturedAt: null,
    useExpireAt: null,
    isCurrent: true,
  });

  const loadValves = () => {
    if (!rid) return;

    API.get(`/tanks/${rid}/valves`).then(({ success, data }) => {
      if (success) {
        setValves(data.valves);
      }
    });
  };

  useEffect(() => {
    if (open) {
      loadValves();
    }
  }, [open, rid]);

  useEffect(() => {
    if (!form.manufacturedAt) {
      return;
    }

    setForm((prev) => ({
      ...prev,
      useExpireAt: prev.manufacturedAt.add(5, 'years'),
    }));
  }, [form.manufacturedAt]);

  const handleChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const openCreate = () => {
    setEditingValve(null);
    setForm({
      serialNo: '',
      manufacturer: '',
      version: '',
      manufacturedAt: null,
      useExpireAt: null,
      isCurrent: true,
    });
    setEditorOpen(true);
  };

  const openEdit = (valve) => {
    setEditingValve(valve);
    setForm({
      serialNo: valve.serialNo ?? '',
      manufacturer: valve.manufacturer ?? '',
      version: valve.version ?? '',
      manufacturedAt: toDayjs(valve.manufacturedAt),
      useExpireAt: toDayjs(valve.useExpireAt),
      isCurrent: valve.isCurrent,
    });
    setEditorOpen(true);
  };

  const validate = () => {
    if (form.serialNo.trim() === '') {
      message.error('밸브 일련번호가 입력되지 않았습니다.');
      return false;
    }
    if (form.manufacturer === '') {
      message.error('밸브 제조사가 선택되지 않았습니다.');
      return false;
    }
    if (form.version === '') {
      message.error('밸브 버전이 선택되지 않았습니다.');
      return false;
    }
    if (!form.manufacturedAt) {
      message.error('밸브 제조 일자가 선택되지 않았습니다.');
      return false;
    }
    return true;
  };

  const refreshAll = () => {
    loadValves();
    onChanged?.();
  };

  const doSave = () => {
    if (!validate()) {
      return;
    }

    setSaving(true);
    const payload = {
      serialNo: form.serialNo,
      manufacturer: form.manufacturer,
      version: form.version,
      manufacturedAt: form.manufacturedAt.format('YYYYMMDD'),
      useExpireAt: form.useExpireAt ? form.useExpireAt.format('YYYYMMDD') : '',
      isCurrent: form.isCurrent,
    };

    const request = editingValve
      ? API.patch(`/tanks/${rid}/valves/${editingValve.rid}`, payload)
      : API.post(`/tanks/${rid}/valves`, payload);

    request.then(({ success }) => {
      setSaving(false);

      if (!success) {
        message.error('저장을 실패했습니다.');
        return;
      }

      setEditorOpen(false);
      refreshAll();
    });
  };

  const doRemove = (valve) => {
    if (!window.confirm('밸브 이력을 삭제하시겠습니까?')) {
      return;
    }

    API.delete(`/tanks/${rid}/valves/${valve.rid}`).then(({ success }) => {
      if (!success) {
        message.error('삭제를 실패했습니다.');
        return;
      }

      refreshAll();
    });
  };

  const columns = [
    {
      title: '현재',
      key: 'isCurrent',
      dataIndex: 'isCurrent',
      width: 90,
      align: 'center',
      render: (_, record) =>
        record.isCurrent ? <Tag color="blue">현재</Tag> : <span className={cx('dash')}>-</span>,
    },
    {
      title: '버전',
      key: 'version',
      dataIndex: 'version',
      width: 120,
      sorter: (a, b) => (a.version ?? '').localeCompare(b.version ?? ''),
      render: (_, record) => record.version ?? '-',
    },
    {
      title: '일련번호',
      key: 'serialNo',
      dataIndex: 'serialNo',
      width: 180,
      sorter: (a, b) => (a.serialNo ?? '').localeCompare(b.serialNo ?? ''),
      render: (_, record) => record.serialNo ?? '-',
    },
    {
      title: '제조사',
      key: 'manufacturer',
      dataIndex: 'manufacturer',
      width: 120,
      sorter: (a, b) => (a.manufacturer ?? '').localeCompare(b.manufacturer ?? ''),
      render: (_, record) => record.manufacturer ?? '-',
    },
    {
      title: '밸브 제조 일자',
      key: 'manufacturedAt',
      dataIndex: 'manufacturedAt',
      width: 140,
      sorter: (a, b) => (a.manufacturedAt ?? '').localeCompare(b.manufacturedAt ?? ''),
      render: (_, record) =>
        record.manufacturedAt
          ? dayjs(record.manufacturedAt, 'YYYYMMDD').format('YYYY-MM-DD')
          : '-',
    },
    {
      title: '밸브 사용 기한',
      key: 'useExpireAt',
      dataIndex: 'useExpireAt',
      width: 140,
      sorter: (a, b) => (a.useExpireAt ?? '').localeCompare(b.useExpireAt ?? ''),
      render: (_, record) =>
        record.useExpireAt
          ? dayjs(record.useExpireAt, 'YYYYMMDD').format('YYYY-MM-DD')
          : '-',
    },
    {
      title: '수정일',
      key: 'updatedAt',
      dataIndex: 'updatedAt',
      width: 180,
      sorter: (a, b) => dayjs(a.updatedAt).valueOf() - dayjs(b.updatedAt).valueOf(),
      render: (_, record) =>
        record.updatedAt
          ? dayjs(record.updatedAt).format('YYYY-MM-DD HH:mm:ss')
          : '-',
    },
    {
      title: '관리',
      key: 'action',
      width: 160,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button size="small" onClick={() => openEdit(record)}>
            수정
          </Button>
          <Button size="small" danger disabled={record.isCurrent} onClick={() => doRemove(record)}>
            삭제
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Modal
        title="밸브 이력"
        open={open}
        onCancel={onClose}
        footer={[
          <Button key="close" onClick={onClose}>
            닫기
          </Button>,
        ]}
        width={1180}
        destroyOnClose={false}
      >
        <div className={cx('contentWrapper')}>
          <Card className={cx('panel')} title="용기 정보">
            <Row gutter={[24, 16]}>
              <Col span={8}>
                <div className={cx('field')}>
                  <span className={cx('label')}>QR 번호</span>
                  <span className={cx('value')}>{tank?.qrcode ?? '-'}</span>
                </div>
              </Col>
              <Col span={8}>
                <div className={cx('field')}>
                  <span className={cx('label')}>용기 일련번호</span>
                  <span className={cx('value')}>{tank?.serialNo ?? '-'}</span>
                </div>
              </Col>
              <Col span={8}>
                <div className={cx('field')}>
                  <span className={cx('label')}>현재 밸브 버전</span>
                  <span className={cx('value')}>{tank?.velveVersion ?? '-'}</span>
                </div>
              </Col>
            </Row>
          </Card>

          <Card
            className={cx('panel')}
            title="밸브 이력"
            extra={
              <Button type="primary" onClick={openCreate}>
                밸브 등록
              </Button>
            }
          >
            <Table
              rowKey="rid"
              columns={columns}
              dataSource={valves}
              pagination={false}
              scroll={{ x: 1150 }}
            />
          </Card>
        </div>
      </Modal>

      <Modal
        title={editingValve ? '밸브 이력 수정' : '밸브 이력 등록'}
        open={editorOpen}
        onCancel={() => setEditorOpen(false)}
        onOk={doSave}
        okText="저장"
        cancelText="취소"
        confirmLoading={saving}
        destroyOnClose={false}
      >
        <Form layout="vertical">
          <Form.Item label="밸브 일련번호" required>
            <Input
              value={form.serialNo}
              onChange={(e) => handleChange('serialNo', e.target.value)}
            />
          </Form.Item>
          <Form.Item label="밸브 제조사" required>
            <Select
              placeholder="선택"
              value={form.manufacturer || null}
              onChange={(value) => handleChange('manufacturer', value)}
              options={valveManufacturerOptions}
            />
          </Form.Item>
          <Form.Item label="밸브 제조 일자" required>
            <DatePicker
              value={form.manufacturedAt}
              onChange={(value) => handleChange('manufacturedAt', value)}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item label="밸브 버전" required>
            <Select
              placeholder="선택"
              value={form.version || null}
              onChange={(value) => handleChange('version', value)}
              options={valveVersionOptions}
            />
          </Form.Item>
          <Form.Item label="밸브 사용 기한">
            <DatePicker value={form.useExpireAt} disabled style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="현재 밸브로 설정">
            <Switch
              checked={form.isCurrent}
              onChange={(checked) => handleChange('isCurrent', checked)}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ValveHistoryModal;
